import React from "react";

import { AssignmentsCompleteSVG, Icon, LockHollowSVG } from "@newsela/angelou";

import { getPrintAssignmentLink } from "@/assignments/utils/assignmentUtils";
import { EventProperties } from "@/types/models/EventProperties.interface";
import ActionsDropdown from "@/view/ActionsDropdown";
import { PrintOption } from "../types/PrintOption.interface";
import getPrintButtonEventProperties from "../utils/getPrintButtonEventProperties";
import {
  anchorCss,
  anchorIconCss,
  assignmentAnchorCss,
  assignmentIconCss,
  lockIconCss,
  printItemCss,
  subscriptionMsgCSS,
  msgHeadingCss,
} from "./PopOutContents.style";

interface PopOutContent {
  assignmentId?: string | undefined;
  baseUrl: string;
  currentLevelId: string;
  eventProperties: EventProperties;
  isMobile?: boolean | undefined;
  isPrintDisabled: boolean;
  printOptions: PrintOption[];
}

const PopOutContents = ({
  assignmentId,
  baseUrl,
  currentLevelId,
  eventProperties,
  isMobile,
  isPrintDisabled,
  printOptions,
}: PopOutContent) => {
  const disabledProps = {
    ariaProps: {
      "aria-disabled": true,
      role: "link",
    },
  };

  const getHref = (baseUrl: string, option: PrintOption) =>
    `${baseUrl}${option?.queryParam ?? ""}`;

  const enabledProps = (option: PrintOption) => ({
    href: getHref(baseUrl, option),
    openInNewTab: true,
  });

  const SubscriptionRequiredMessage = () => (
    <div css={subscriptionMsgCSS(isMobile)}>
      <p css={msgHeadingCss}>Subscription needed</p>
      <p>
        {" "}
        Unlimited printing requires a school subscription. Request school access
        to put us in touch with your administrator.
      </p>
    </div>
  );

  const printAssignmentLink = assignmentId
    ? getPrintAssignmentLink(assignmentId, currentLevelId)
    : null;

  const renderPrintAssignmentOption = () => {
    if (!assignmentId) {
      return null;
    }

    const eventProperties = {
      objectType: "Assignment",
      actionPrefix: "Initiate",
      actionName: "Print",
      streamProperties: {
        content_type: "Assignment",
        content_id: currentLevelId,
        object_id: assignmentId,
      },
    };

    return (
      <React.Fragment key={`print-assignment-${assignmentId}`}>
        {/* @ts-expect-error Property 'onClick' is not needed for this component */}
        <ActionsDropdown.AnchorItem
          __cssFor={{
            Anchor: {
              root: assignmentAnchorCss,
              Icon: { root: anchorIconCss },
            },
          }}
          dataProps={{ "data-qa-selector": "print-assignment-link" }}
          {...eventProperties}
          itemCss={printItemCss(isPrintDisabled)}
          {...(isPrintDisabled
            ? { ...disabledProps }
            : { href: printAssignmentLink })}
          key={`print-assignment-${assignmentId}`}
        >
          {isPrintDisabled ? (
            <Icon
              __cssFor={{ root: lockIconCss(isPrintDisabled) }}
              isDecorative={false}
              size={Icon.sizes.md}
              SvgComponent={LockHollowSVG}
              title="locked"
            />
          ) : (
            <Icon
              __cssFor={{ root: assignmentIconCss }}
              isDecorative
              size={Icon.sizes.md}
              SvgComponent={AssignmentsCompleteSVG}
            />
          )}
          Assignment
        </ActionsDropdown.AnchorItem>
      </React.Fragment>
    );
  };

  return (
    <>
      {isPrintDisabled && <SubscriptionRequiredMessage />}
      {renderPrintAssignmentOption()}
      {printOptions.map((option) => {
        if (!option) return null;

        return (
          <React.Fragment key={option.label}>
            {/* @ts-expect-error Property 'onClick' is not needed for this component */}
            <ActionsDropdown.AnchorItem
              __cssFor={{
                Anchor: { root: anchorCss, Icon: { root: anchorIconCss } },
              }}
              dataProps={{
                "data-qa-selector": `print-link-${option?.queryParam ?? ""}`,
              }}
              eventProperties={getPrintButtonEventProperties(
                option,
                eventProperties,
              )}
              itemCss={printItemCss(isPrintDisabled)}
              {...(isPrintDisabled
                ? { ...disabledProps }
                : { ...enabledProps(option) })}
            >
              {isPrintDisabled ? (
                <Icon
                  __cssFor={{ root: lockIconCss(isPrintDisabled) }}
                  isDecorative={false}
                  size={Icon.sizes.md}
                  SvgComponent={LockHollowSVG}
                  title="locked"
                />
              ) : null}
              {option.label}
            </ActionsDropdown.AnchorItem>
          </React.Fragment>
        );
      })}
    </>
  );
};

export default PopOutContents;
